import {useRef, useState} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {logger} from '@/helpers/logger';
import {updateRack, deleteRack} from '@/services/rackCatalog';
import {useRackModalBase} from './useRackModalBase';

const buildInitialImages = (imageUrls) =>
  (imageUrls || []).map((url, index) => ({
    name: url.split('/').pop() || `image-${index + 1}`,
    size: 0,
    lastModified: Date.now(),
    imageUrl: url
  }));

const buildInitialInfo = (rack) => ({
  rackId: rack.rackId || '',
  displayName: rack.displayName || '',
  description: rack.description || '',
  materialCode: rack.material || '',
  category: rack.category || '',
  capacity: rack.capacity || '',
  width: rack.width || '',
  depth: rack.depth || '',
  height: rack.height || '',
  points: rack.pointsForRack != null ? String(rack.pointsForRack) : ''
});

export const useEditRackModal = ({rack, onSuccess} = {}) => {
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const saveInProgress = useRef(false);
  const deleteInProgress = useRef(false);

  const base = useRackModalBase({
    initialImages: () => buildInitialImages(rack?.imageUrls),
    initialInfo: () => buildInitialInfo(rack || {}),
    actionInProgress: saveInProgress
  });

  const {
    images,
    rackInformation,
    isUploading,
    imageLimitReached,
    setPublishError
  } = base;

  const uploadDisabled =
    isUploading || isSaving || isDeleting || imageLimitReached;
  const canSave =
    !isUploading &&
    !isSaving &&
    !isDeleting &&
    Number.isFinite(Number(rackInformation.points)) &&
    ['displayName', 'points'].every(
      (name) => String(rackInformation[name] ?? '').trim().length > 0
    );

  const handleSave = async () => {
    if (saveInProgress.current) return;
    if (!canSave) {
      setPublishError(
        'Fill in Rack Display Name and valid Points for the Rack before saving.'
      );
      return;
    }
    saveInProgress.current = true;
    setIsSaving(true);
    setPublishError('');
    let response;
    try {
      response = await updateRack(rack.id, {
        data: {
          displayName: rackInformation.displayName.trim(),
          imageUrls: images.map((image) => image.imageUrl),
          description: rackInformation.description?.trim() || '',
          material: rackInformation.materialCode?.trim() || '',
          category: rackInformation.category?.trim() || '',
          capacity: rackInformation.capacity?.trim() || '',
          width: rackInformation.width?.trim() || '',
          depth: rackInformation.depth?.trim() || '',
          height: rackInformation.height?.trim() || '',
          pointsForRack: Number(rackInformation.points),
          isActive: true,
          updatedBy: ''
        }
      });
      if (response?.success === false) {
        throw new Error(
          response.errorMessage ||
            response.message ||
            'Rack could not be updated.'
        );
      }
      queryClient.invalidateQueries({queryKey: ['rackCatalog']});
    } catch (error) {
      logger.error('Rack update failed:', error);
      setPublishError(
        error?.response?.data?.errorMessage ||
          error?.response?.data?.message ||
          error?.message ||
          'Rack could not be updated. Please try again.'
      );
      return;
    } finally {
      saveInProgress.current = false;
      setIsSaving(false);
    }
    onSuccess?.(response);
  };

  const handleDeleteRequest = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  const handleDeleteConfirm = async () => {
    if (deleteInProgress.current) return;
    deleteInProgress.current = true;
    setIsDeleting(true);
    setPublishError('');
    try {
      const response = await deleteRack(rack.id);
      if (response?.success === false) {
        throw new Error(
          response.errorMessage ||
            response.message ||
            'Rack could not be deleted.'
        );
      }
      queryClient.invalidateQueries({queryKey: ['rackCatalog']});
      onSuccess?.(response);
    } catch (error) {
      logger.error('Rack delete failed:', error);
      setPublishError(
        error?.response?.data?.errorMessage ||
          error?.response?.data?.message ||
          error?.message ||
          'Rack could not be deleted. Please try again.'
      );
      setShowDeleteConfirm(false);
    } finally {
      deleteInProgress.current = false;
      setIsDeleting(false);
    }
  };

  return {
    ...base,
    isSaving,
    isDeleting,
    showDeleteConfirm,
    uploadDisabled,
    canSave,
    handleSave,
    handleDeleteRequest,
    handleDeleteCancel,
    handleDeleteConfirm,
    handleUploadClick: () => base.handleUploadClick(uploadDisabled)
  };
};
